import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faUpload, 
  faDownload, 
  faCheckCircle,
  faExclamationTriangle,
  faFileAlt
} from '@fortawesome/free-solid-svg-icons'
import './CSVUpload.css'

const CSVUpload = ({ 
  title, 
  onFileUpload, 
  templateData, 
  uploadedFile, 
  previewData,
  isLoading = false,
  error = null 
}) => {

  const [dragOver, setDragOver] = useState(false)

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      handleFile(file)
    }
  }

  const handleFile = (file) => {
    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file')
      return
    }
    
    onFileUpload(file)
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (event) => {
    event.preventDefault()
    setDragOver(false)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    setDragOver(false)
    
    const files = event.dataTransfer.files
    if (files.length > 0) {
      handleFile(files[0])
    }
  }

  const downloadTemplate = () => {
    if (!templateData) return
    
    const blob = new Blob([templateData], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${title.toLowerCase().replace(/\s+/g, '_')}_template.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const parseCSVForPreview = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim())
    if (lines.length === 0) return { headers: [], rows: [] }
    
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''))
    const rows = lines.slice(1, 6).map(line => 
      line.split(',').map(cell => cell.trim().replace(/"/g, ''))
    )
    
    return { headers, rows }
  }

  return (
    <div className="csv-upload-container">
      <div className="csv-upload-header">
        <h3>
          <FontAwesomeIcon icon={faFileAlt} className="title-icon" />
          {title}
        </h3>
        {templateData && (
          <button 
            className="template-download-btn"
            onClick={downloadTemplate}
            title="Download CSV template"
          >
            <FontAwesomeIcon icon={faDownload} />
            Template
          </button>
        )}
      </div>

      <div 
        className={`csv-upload-zone ${dragOver ? 'drag-over' : ''} ${uploadedFile ? 'has-file' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="csv-file-input"
          id={`csv-input-${title.replace(/\s+/g, '-').toLowerCase()}`}
          disabled={isLoading}
        />
        
        <label 
          htmlFor={`csv-input-${title.replace(/\s+/g, '-').toLowerCase()}`}
          className="csv-upload-label"
        >
          {isLoading ? (
            <div className="upload-loading">
              <div className="spinner"></div>
              <span>Processing...</span>
            </div>
          ) : uploadedFile ? (
            <div className="upload-success">
              <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
              <div className="file-info">
                <span className="file-name">{uploadedFile.name}</span>
                <span className="file-size">
                  {(uploadedFile.size / 1024).toFixed(1)} KB
                </span>
              </div>
            </div>
          ) : (
            <div className="upload-prompt">
              <FontAwesomeIcon icon={faUpload} className="upload-icon" />
              <span className="upload-text">
                Drop CSV file here or click to browse
              </span>
              <span className="upload-hint">
                Supports CSV files only
              </span>
            </div>
          )}
        </label>
      </div>

      {error && (
        <div className="csv-upload-error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
        </div>
      )}
      
      {previewData?.totalRows && (
        <div className="file-row-count">
          <span>{previewData.totalRows} rows found</span>
        </div>
      )}
    </div>
  )
}

export default CSVUpload